
import { Vue, Component, Watch } from "vue-property-decorator";
import Booking from "@/domain/booking/Booking";
import Restaurant from "@/domain/restaurant/Restaurant";
import RemoteServices from "@/services/RemoteServices";
import Loading from "@/components/app/Loading.vue";
import TimeService from "@/services/TimeService";
import Room from "@/domain/restaurant/Room";
import ValueCardSelector from "@/components/booking/components/ValueCardSelector.vue";
import BookingDetailsForm from "@/components/booking/components/BookingDetailsForm.vue";
import BookingDetailsOnlineForm from "@/components/booking/online/BookingDetailsOnlineForm.vue";
import PrivacyWarning from "@/components/app/PrivacyWarning.vue";
import LogoAppBar from "@/components/app/LogoAppBar.vue";
import RestaurantContactCard from "@/components/restaurant/RestaurantContactCard.vue";
import ReCaptchaV3 from "@/domain/ReCaptchaV3";
@Component({
  components: {
    RestaurantContactCard,
    LogoAppBar,
    PrivacyWarning,
    BookingDetailsOnlineForm,
    BookingDetailsForm,
    ValueCardSelector,
    Loading
  }
})
export default class PublicBookingView extends Vue {
  step = 1;

  acceptsOnlineBookings: boolean = true;

  restaurant: Restaurant = new Restaurant();

  booking: Booking | null = new Booking();

  selectedDate: string = TimeService.getISODateString(new Date());

  pickerDateSync = this.selectedDate;

  fetchedMonths: number[] = [new Date().getUTCMonth()];

  loadingDates = true;

  loadingTimes = true;

  blockedDates: string[] = [];

  allowedPartySizes: number[] = [];

  availableTimes: string[] = [];

  selectedDateTime: string | null = null;

  detailsFormValid: boolean = false;

  successDialog: boolean = false;

  emptyMessage = "Select Date And Party Size";

  errorMessage = "Something Went Wrong, Please Try Again Later";

  recaptcha = new ReCaptchaV3((process.env.VUE_APP_RECAPTCHA_KEY!));

  cookieSnackbar = true;

  async created() {
    await this.getRestaurantPublic();
  }

  get timeDisplayString() {
    if (this.selectedDateTime != null && this.selectedDateTime !== "")
      return TimeService.getTimeFromISOString(this.selectedDateTime);
    return "";
  }


  changeEmptyTimeMessage() {
    this.emptyMessage = "No Times Available";
  }

  @Watch("pickerDateSync")
  updateSelectedMonth() {
    const newMonth = new Date(this.pickerDateSync).getUTCMonth();
    if (!this.fetchedMonths.includes(newMonth)) {
      this.getBlockedDatesByMonth(newMonth);
      this.fetchedMonths.push(newMonth);
    }
  }

  async getCaptchaToken(action: string) {
    return await this.recaptcha.getToken(action);
  }

  get formValid() {
    return (
      this.selectedDate != null &&
      this.booking != null &&
      this.booking.numberOfPeople != null &&
      this.booking.preferredRoomId != null
    );
  }

  get hasDateTime() {
    return this.selectedDateTime != null && this.selectedDateTime !== "";
  }



  get showAltLabels() {
    switch (this.$vuetify.breakpoint.name) {
      case "xs":
        return true;
      case "sm":
      case "md":
      case "lg":
      case "xl":
        return false;
    }
  }



  @Watch("selectedDate")
  refreshTimesOnDateChange() {
    this.getAvailableTimes();
  }

  refreshTimesOnRoomChange() {
    console.log("Room change");
    if (this.booking && this.booking.preferredRoomId == null)
      this.availableTimes = [];
    else this.getAvailableTimes();
  }

  @Watch("booking.numberOfPeople")
  refreshTimesOnNPeopleChange() {
    this.getAvailableTimes();
  }

  @Watch("formValid")
  async getAvailableTimes() {
    if (
      this.booking &&
      this.formValid &&
      this.allowedDates(this.selectedDate)
    ) {
      this.loadingTimes = true;
      try {
        this.availableTimes = [];
        this.availableTimes = await RemoteServices.getAvailableTimesByDatePublic(
          this.booking.preferredRoomId!,
          this.booking.numberOfPeople,
          this.selectedDate
        );

        this.changeEmptyTimeMessage();
      } catch (error) {
        await this.$store.dispatch("error", error.message);
        console.log(error.message);
        this.availableTimes = [];
      }
      this.selectedDateTime = "";
    } else this.availableTimes = [];
    this.loadingTimes = false;
  }

  async getRestaurantPublic() {
    await this.$store.dispatch("loading");
    if (this.$route.query.restId != null) {
      try {
        this.restaurant = await RemoteServices.getRestaurantPublic(
          parseInt(this.$route.query.restId as string)
        );
        if (this.restaurant.rooms.length === 0)
          this.acceptsOnlineBookings = false;

        await this.getBlockedDatesByMonth(new Date().getUTCMonth());
        this.setStartDate();
        this.potentialPartySizes();
        this.setRoomDefault();
      } catch (error) {
        console.log(error.message);
        await this.$router.push("404");
      }
    } else {
      await this.$router.push("404");
    }
    await this.$store.dispatch("clearLoading");
  }

  setRoomDefault() {
    if (
      this.restaurant.rooms != null &&
      this.restaurant.rooms.length > 0 &&
      this.booking
    )
      this.booking.preferredRoomId = this.restaurant.rooms[0].roomId;
  }

  setStartDate() {
    let date = new Date();
    while (date.getUTCMonth() === new Date().getUTCMonth()) {
      if (this.allowedDates(TimeService.getISODateString(date))) {
        this.selectedDate = TimeService.getISODateString(date);
        return;
      } else {
        date = TimeService.addDaysToDate(date, 1);
      }
    }
  }

  async getBlockedDatesByMonth(month: number) {
    if (this.selectedDate != null && this.selectedDate !== "") {
      try {
        this.loadingDates = true;
        const date = new Date();
        date.setUTCMonth(month);
        if (this.pickerDateSync != null)
          date.setUTCFullYear(new Date(this.pickerDateSync).getUTCFullYear());

        this.blockedDates.push(
          ...(await RemoteServices.getRestaurantBlockedDatesByMonth(
            this.restaurant.restaurantId,
            TimeService.getISODateString(date)
          ))
        );
      } catch (error) {
        await this.$store.dispatch("error", this.errorMessage);
        console.log(error.message);
      }
      this.loadingDates = false;
    }
  }

  allowedDates(date: string) {
    return !(
      this.blockedDates.includes(date) ||
      this.restaurant.rooms.filter(r => Room.isOpenOnDate(r, new Date(date)))
        .length === 0
    );
  }

  potentialPartySizes() {
    this.allowedPartySizes = Array.from(
      Array(this.restaurant.onlineBookingMaxSize).keys()
    ).map(i => i + 1);
  }

  maxBookingDate() {
    const date = new Date();
    return TimeService.getISODateString(TimeService.addDaysToDate(date, 90));
  }

  get availableRooms() {
    return this.restaurant.rooms.filter(r =>
      Room.isOpenOnDate(r, new Date(this.selectedDate))
    );
  }

  setBookingDateTime(dateTime: string) {
    if (this.selectedDateTime === dateTime) this.selectedDateTime = "";
    else {
      this.selectedDateTime = dateTime;
    }
  }

  get dateTimeDisplay() {
    if (this.selectedDateTime != null)
      return (
        TimeService.getDateDisplayStringWithWeekDay(
          new Date(this.selectedDateTime)
        ) +
        " at " +
        TimeService.getTimeFromISOString(this.selectedDateTime)
      );
  }

  async createOnlineBooking() {
    await this.$store.dispatch("loading");
    this.setBookingValues();
    if (this.booking && this.formValid) {
      try {
        const token = await this.getCaptchaToken("newBooking");
        this.booking = await RemoteServices.createOnlineBooking(
          this.restaurant.restaurantId,
          this.booking,
          token
        );
        this.successDialog = true;
        this.step = 1;
      } catch (error) {
        if(error.message)
          await this.$store.dispatch("error", error.message);
        else
          await this.$store.dispatch("error", "Something went wrong, please try again later.");
        console.log(error.message);
      }
    }
    await this.$store.dispatch("clearLoading");
  }

  setBookingValues() {
    if (this.booking != null && this.selectedDateTime != null) {
      this.booking.bookingDateTime = this.selectedDateTime;
    }
  }

  async resetPage() {
    await this.$store.dispatch("loading");
    this.successDialog = false;
    this.step = 1;
    this.selectedDateTime = null;
    this.availableTimes = [];
    this.emptyMessage = "Select Date And Party Size";
    this.booking = new Booking();
    await this.$store.dispatch("clearLoading");
  }
}
